import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Truck, 
  BarChart3, Settings, LogOut, Menu, X, ChevronLeft,
  Store, Link2, Download, Megaphone, Heart, Rocket, FileText, Brain, Compass, Crown, Ship, AlertTriangle, Factory,
  Anchor, MessageSquareText, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/products', icon: Package, label: 'Products' },
    { href: '/admin/integrity', icon: ShieldCheck, label: 'Integrity Lock' },
    { href: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { href: '/admin/fulfillment', icon: Factory, label: 'Fulfillment' },
    { href: '/admin/autopilot', icon: Rocket, label: 'Autopilot' },
    { href: '/admin/disputes', icon: AlertTriangle, label: 'Disputes' },
    { href: '/admin/vendors', icon: Ship, label: 'Vendor Network' },
    { href: '/admin/suppliers', icon: Truck, label: 'Suppliers' },
    { href: '/admin/inventory', icon: BarChart3, label: 'Inventory' },
    { href: '/admin/integrations', icon: Link2, label: 'Integrations' },
    { href: '/admin/import', icon: Download, label: 'Import Products' },
    { href: '/admin/marketing', icon: Megaphone, label: 'AI Marketing' },
    { href: '/admin/coach', icon: Heart, label: 'AI Coach' },
    { href: '/admin/aisha', icon: Brain, label: 'A.I_SHA' },
    { href: '/admin/aisha/command', icon: MessageSquareText, label: 'Command Bridge' },
    { href: '/admin/aisha/prospects', icon: Anchor, label: 'Prospect Pipeline' },
    { href: '/admin/aisha/platforms', icon: KeyRound, label: 'Credentials Vault' },
    { href: '/admin/aisha/chief-executive', icon: Crown, label: 'Chief Executive' },
    { href: '/admin/aisha/smartsearch', icon: Compass, label: 'SmartSearch' },
    { href: '/admin/launch', icon: Rocket, label: 'Launch Checklist' },
    { href: '/admin/content', icon: FileText, label: 'Content Templates' },
  ];

  const isActive = (href) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-6 w-6 text-slate-900" />
          <span className="font-heading font-bold text-slate-900">Admin</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 h-full bg-white border-r border-slate-200 transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-64',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
          {!sidebarCollapsed && (
            <Link to="/admin" className="flex items-center gap-2">
              <Package className="h-6 w-6 text-slate-900" />
              <span className="font-heading font-bold text-slate-900">Admin</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn('h-4 w-4 transition-transform', sidebarCollapsed && 'rotate-180')} />
          </Button>
        </div>

        {/* Navigation - scrollable with bottom padding to prevent overlap */}
        <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive(item.href)
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Bottom section - fixed height */}
        <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-slate-200 bg-white">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            data-testid="back-to-store"
          >
            <Store className="h-5 w-5 flex-shrink-0" />
            {!sidebarCollapsed && <span>Back to Store</span>}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          'transition-all duration-300 pt-14 lg:pt-0',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        {/* Top bar */}
        <div className="hidden lg:flex h-14 items-center justify-between px-6 bg-white border-b border-slate-200">
          <h1 className="font-heading text-lg font-bold text-slate-900">
            {navItems.find(item => isActive(item.href))?.label || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-sm font-medium text-slate-600">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
